import React, {
  CSSProperties,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Project } from "../data/projects";
import "./ProjectArtwork.css";

type Glyph = "audio" | "code" | "tree" | "chat";

function Icon({
  name,
  x = 0,
  y = 0,
  size = 28,
}: {
  name: Glyph;
  x?: number;
  y?: number;
  size?: number;
}) {
  const shapes: Record<Glyph, React.ReactNode> = {
    audio: (
      <>
        <rect x="9" y="2" width="6" height="13" rx="3" />
        <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3m-4 0h8" />
      </>
    ),
    code: (
      <>
        <path d="m7 5-7 7 7 7m10-14 7 7-7 7" />
      </>
    ),
    tree: (
      <>
        <rect x="9" y="2" width="6" height="6" rx="1" />
        <rect x="1" y="17" width="6" height="6" rx="1" />
        <rect x="17" y="17" width="6" height="6" rx="1" />
        <path d="M12 8v5H4v4m8-4h8v4" />
      </>
    ),
    chat: (
      <>
        <path d="M21 14a4 4 0 0 1-4 4H9l-6 4v-6a4 4 0 0 1-2-4V6a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4Z" />
        <path d="M6 8h10M6 12h6" />
      </>
    ),
  };
  return (
    <svg
      x={x}
      y={y}
      width={size}
      height={size}
      viewBox="-1 -1 26 26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {shapes[name]}
    </svg>
  );
}

const socialPaths: Record<string, string> = {
  youtube:
    "M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z",
  instagram:
    "M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z",
  facebook:
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2m13 2h-2.5A3.5 3.5 0 0 0 12 8.5V11h-2v3h2v7h3v-7h3v-3h-3V9a1 1 0 0 1 1-1h2V5z",
  linkedin:
    "M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z",
  tiktok:
    "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z",
  x: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
};

function Tile({
  x,
  y,
  label,
  icon,
  social,
  color,
  width = 76,
  delay = 0,
  fill,
}: {
  x: number;
  y: number;
  label: string;
  icon?: Glyph;
  social?: string;
  color?: string;
  width?: number;
  delay?: number;
  fill: string;
}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g
        className="va-tile va-float"
        style={
          {
            "--va-delay": `${delay}s`,
            color: color || "var(--va-accent)",
          } as CSSProperties
        }
      >
        <rect
          width={width}
          height="76"
          rx="21"
          fill={`url(#${fill})`}
          className="va-tile-panel"
        />
        {icon && <Icon name={icon} x={(width - 28) / 2} y={13} />}
        {social && (
          <svg
            x={(width - 29) / 2}
            y="12"
            width="29"
            height="29"
            viewBox="0 0 24 24"
          >
            <path fill="currentColor" d={socialPaths[social]} />
          </svg>
        )}
        <text
          x={width / 2}
          y="59"
          textAnchor="middle"
          className="va-tile-label"
        >
          {label}
        </text>
      </g>
    </g>
  );
}

function Frame({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#29323c" />
          <stop offset="1" stopColor="#172029" />
        </linearGradient>
        <linearGradient
          id={`${id}-flow`}
          x1="0"
          y1="0"
          x2="500"
          y2="500"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--va-accent)" />
          <stop offset="1" stopColor="var(--va-secondary)" />
        </linearGradient>
        <radialGradient id={`${id}-halo`}>
          <stop stopColor="var(--va-accent)" stopOpacity=".13" />
          <stop offset="1" stopColor="var(--va-accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="250" cy="250" r="205" fill={`url(#${id}-halo)`} />
      <ellipse
        cx="250"
        cy="253"
        rx="219"
        ry="208"
        className="va-ring va-ring-outer"
        transform="rotate(-18 250 253)"
      />
      <circle cx="250" cy="250" r="142" className="va-ring va-ring-inner" />
    </>
  );
}

function Connections({
  id,
  destinations,
}: {
  id: string;
  destinations: number[][];
}) {
  return (
    <g
      className="va-connections"
      fill="none"
      stroke={`url(#${id}-flow)`}
      strokeWidth="1.4"
      strokeDasharray="4 9"
    >
      {destinations.map(([x, y], i) => (
        <path
          key={i}
          className="va-flow"
          d={`M250 245 Q${(250 + x) / 2} ${y} ${x} ${y}`}
        />
      ))}
    </g>
  );
}

function HeaderLabel({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon: Glyph;
}) {
  return (
    <g transform="translate(65 13)" className="va-header-label">
      <Icon name={icon} size={18} />
      <text x="28" y="14">
        {children}
      </text>
      <circle cx="130" cy="9" r="3" fill="var(--va-accent)" />
    </g>
  );
}

function GruviScene({ id }: { id: string }) {
  const nodes = [
    { x: 75, y: 60, social: "youtube", label: "YouTube", color: "#ff5757" },
    {
      x: 324,
      y: 25,
      social: "instagram",
      label: "Instagram",
      color: "#f07bab",
    },
    { x: 424, y: 205, social: "tiktok", label: "TikTok", color: "#54e2d6" },
    { x: 344, y: 370, social: "facebook", label: "Facebook", color: "#6aa9ff" },
    { x: 70, y: 380, social: "linkedin", label: "LinkedIn", color: "#74b6ef" },
    { x: 0, y: 215, social: "x", label: "X", color: "#e5e7eb" },
  ];
  return (
    <>
      <Frame id={id} />
      <Connections
        id={id}
        destinations={nodes.map((n) => [n.x + 38, n.y + 38])}
      />
      <HeaderLabel icon="code">Your agent</HeaderLabel>
      <g className="va-mascot-float">
        <image
          href="/images/heroes/gruvi-mascot.png"
          x="160"
          y="148"
          width="180"
          height="180"
          className="va-gruvi-mascot"
        />
      </g>
      <text x="250" y="374" textAnchor="middle" className="va-product-name">
        Gruvi
      </text>
      {nodes.map((node, i) => (
        <Tile
          key={node.social}
          {...node}
          fill={`${id}-panel`}
          delay={i * -0.7}
        />
      ))}
      <g transform="translate(154 444)" className="va-post-card">
        <rect width="259" height="66" rx="17" />
        <clipPath id={`${id}-post-clip`}>
          <rect x="11" y="10" width="36" height="45" rx="7" />
        </clipPath>
        <image
          href="/images/heroes/gruvi-post.jpeg"
          x="11"
          y="10"
          width="36"
          height="45"
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${id}-post-clip)`}
        />
        <text x="60" y="28" className="va-card-title">
          Your next post
        </text>
        <text x="60" y="48" className="va-card-caption">
          Video + thumbnail
        </text>
        <path d="m225 25 12 8-12 8Z" fill="var(--va-accent)" />
      </g>
    </>
  );
}

function Butterfly({ id }: { id: string }) {
  return (
    <g className="va-butterfly-lift">
      <svg
        x="330"
        y="51"
        width="99"
        height="99"
        viewBox="0 0 104 104"
        className="va-butterfly"
      >
        <defs>
          <linearGradient id={`${id}-wing`} x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#fff4d4" />
            <stop offset=".55" stopColor="#edc87b" />
            <stop offset="1" stopColor="#c48c51" />
          </linearGradient>
        </defs>
        <g fill={`url(#${id}-wing)`}>
          <g className="va-wing va-wing-left">
            <path d="M7 9C36 9 62 35 63 70C58 52 39 61 25 49C15 40 17 26 7 9Z" />
            <path d="M38 62C43 59 56 65 59 72C64 82 41 91 35 93C46 78 32 68 38 62Z" />
          </g>
          <g className="va-wing va-wing-right">
            <path d="M96 18C77 30 68 54 67 70C74 53 86 57 90 37C92 29 94 22 96 18Z" />
            <path d="M71 72C83 58 77 79 86 93C78 89 63 80 71 72Z" />
          </g>
        </g>
      </svg>
    </g>
  );
}

function FableScene({ id }: { id: string }) {
  return (
    <>
      <defs>
        <radialGradient id={`${id}-story-halo`}>
          <stop stopColor="#c5a1eb" stopOpacity=".2" />
          <stop offset="1" stopColor="#c5a1eb" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-imagination`} x1="0" y1="1" x2="1" y2="0">
          <stop stopColor="#9d7ac4" stopOpacity="0" />
          <stop offset=".5" stopColor="#f3cb85" stopOpacity=".7" />
          <stop offset="1" stopColor="#f3cb85" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-prompt`}>
          <rect
            className="va-prompt-reveal"
            x="56"
            y="75"
            width="214"
            height="23"
          />
        </clipPath>
      </defs>
      <circle cx="250" cy="285" r="225" fill={`url(#${id}-story-halo)`} />
      <text x="42" y="42" className="va-scene-kicker">
        AI STORYTELLING
      </text>
      <g className="va-story-prompt">
        <rect x="42" y="66" width="235" height="40" rx="12" />
        <g clipPath={`url(#${id}-prompt)`}>
          <text x="56" y="91">
            A fox finds a mysterious letter...
          </text>
        </g>
      </g>
      <path
        d="M210 109Q205 133 228 150"
        className="va-story-trail va-flow"
        fill="none"
        stroke="#d5b080"
        strokeWidth="1.3"
        strokeDasharray="2 6"
      />
      <path
        d="M241 448C95 365 471 315 382 129"
        className="va-story-trail va-flow"
        fill="none"
        stroke={`url(#${id}-imagination)`}
        strokeWidth="1.5"
        strokeDasharray="3 10"
      />
      <g className="va-living-storybook">
        <image
          href="/images/heroes/fable-storyworld-thin-binding.webp"
          x="20"
          y="85"
          width="460"
          height="420"
          className="va-storyworld-image"
        />
      </g>
      <Butterfly id={id} />
      {[
        [304, 94],
        [433, 161],
        [84, 254],
        [391, 119],
        [64, 195],
        [442, 332],
      ].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y})`}>
          <path
            className="va-spark"
            style={{ "--va-delay": `${i * -0.8}s` } as CSSProperties}
            d="M0-4 1.2-1.2 4 0 1.2 1.2 0 4-1.2 1.2-4 0-1.2-1.2Z"
            fill="#f5dc9f"
          />
        </g>
      ))}
      <text x="250" y="519" textAnchor="middle" className="va-format-caption">
        READ / LISTEN / WATCH
      </text>
    </>
  );
}

function DNA() {
  const strand = (phase: number) =>
    Array.from(
      { length: 61 },
      (_, i) =>
        `${22 + Math.sin((i / 60) * Math.PI * 4 + phase) * 17},${i * 2}`,
    ).join(" ");
  return (
    <g fill="none" strokeWidth="2.1" strokeLinecap="round">
      {Array.from({ length: 13 }, (_, i) => {
        const x = Math.sin((i / 12) * Math.PI * 4) * 17;
        return (
          <path
            key={i}
            className="va-dna-rung"
            style={{ "--va-delay": `${i * -0.18}s` } as CSSProperties}
            d={`M${22 - x} ${i * 10}H${22 + x}`}
            stroke="#dfc28d"
          />
        );
      })}
      <polyline points={strand(0)} stroke="#8dd6b7" />
      <polyline points={strand(Math.PI)} stroke="#d4b581" />
    </g>
  );
}

function VeldScene({ id }: { id: string }) {
  const pedigreePath =
    "M214 108v33h45v33m47-66v33h-47M370 108v33h46v33m47-66v33h-47M259 230v26h79v41m78-67v26h-78";
  return (
    <>
      <defs>
        <linearGradient
          id={`${id}-land`}
          x1="0"
          y1="330"
          x2="0"
          y2="527"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#304c3e" stopOpacity=".6" />
          <stop offset="1" stopColor="#16261e" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-gene`} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#82d5b1" />
          <stop offset="1" stopColor="#d6bb87" />
        </linearGradient>
      </defs>
      <text x="42" y="42" className="va-scene-kicker">
        HERD RECORDS / GENETICS
      </text>
      <g className="va-terrain" fill="none" stroke="#7baf8c" strokeWidth=".8">
        <path d="M-20 404Q80 304 166 362T350 353 530 388M-20 424Q80 324 166 382T350 373 530 408M-20 444Q80 344 166 402T350 393 530 428M-20 464Q80 364 166 422T350 413 530 448M-20 484Q80 384 166 442T350 433 530 468M-20 504Q80 404 166 462T350 453 530 488" />
      </g>
      <path
        d="M-600 445H0Q78 359 181 414T363 411 500 420H1100V550H-600Z"
        fill={`url(#${id}-land)`}
      />
      <g transform="translate(47 97)">
        <rect
          x="-13"
          y="-18"
          width="110"
          height="239"
          rx="55"
          fill="#86b69506"
          stroke="#c0d9ad20"
        />
        <g transform="scale(1.6)">
          <DNA />
        </g>
        <text x="41" y="242" textAnchor="middle" className="va-genome-label">
          GENETICS
        </text>
      </g>
      <g
        className="va-family-branches"
        fill="none"
        stroke={`url(#${id}-gene)`}
        strokeWidth="1.6"
      >
        <path d={pedigreePath} />
        <path
          d={pedigreePath}
          className="va-pedigree-trace"
          strokeWidth="2.4"
        />
        <path
          className="va-flow"
          d="M97 198Q153 198 161 269T266 354"
          strokeDasharray="3 8"
        />
      </g>
      {[
        [193, 69],
        [285, 69],
        [349, 69],
        [441, 69],
      ].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y})`} className="va-ancestor">
          <rect width="43" height="43" rx="13" />
          <Icon name="tree" x={11} y={10} size={22} />
        </g>
      ))}
      <g className="va-pedigree-parent" transform="translate(217 170)">
        <rect width="84" height="64" rx="17" />
        <image
          href="/images/heroes/veld-bull.webp"
          x="6"
          y="8"
          width="48"
          height="48"
        />
        <text x="66" y="35">
          Sire
        </text>
      </g>
      <g className="va-pedigree-parent" transform="translate(374 170)">
        <rect width="84" height="64" rx="17" />
        <image
          href="/images/heroes/veld-bull.webp"
          x="6"
          y="8"
          width="48"
          height="48"
        />
        <text x="66" y="35">
          Dam
        </text>
      </g>
      <g className="va-animal-record">
        <rect
          x="242"
          y="277"
          width="177"
          height="181"
          rx="24"
          fill="#21352b"
          stroke="#b7d8b43d"
        />
        <rect
          x="254"
          y="290"
          width="153"
          height="125"
          rx="17"
          fill="#afc78c0a"
        />
        <image
          href="/images/heroes/veld-bull.webp"
          x="259"
          y="287"
          width="143"
          height="136"
          className="va-veld-bull"
        />
        <circle cx="261" cy="437" r="3" fill="#9cdaa9" />
        <text x="273" y="441">
          Your animal
        </text>
        <path
          className="va-record-scan"
          d="M256 307h150"
          stroke="#b1e4b4"
          strokeOpacity=".55"
          strokeWidth="1"
        />
      </g>
      <g transform="translate(43 373)" className="va-field-note">
        <Icon name="chat" x={0} y={0} size={21} />
        <text x="31" y="13">
          Ask Veld
        </text>
        <text x="0" y="44" className="va-field-question">
          What do these
        </text>
        <text x="0" y="63" className="va-field-question">
          records tell us?
        </text>
        <path d="M0 84h135" stroke="#bfd7ae2e" />
        <text x="0" y="103" className="va-field-source">
          Grounded in your herd.
        </text>
      </g>
      <g className="va-branch-dot">
        <circle cx="338" cy="256" r="4" fill="#bddcad" />
        <circle
          className="va-node-pulse"
          cx="338"
          cy="256"
          r="10"
          fill="none"
          stroke="#b3dbab"
        />
      </g>
    </>
  );
}

const descriptions: Record<string, string> = {
  gruvi:
    "Gruvi connects your agent and finished content to YouTube, Instagram, TikTok, Facebook, LinkedIn and X.",
  fable:
    "A story prompt becomes a living storybook with a fox, a dragon and a miniature forest. Fable’s golden butterfly flies above the pages.",
  veld: "A cattle pedigree grows through generations into an animal record. A DNA helix, field contours and Ask Veld connect genetics to the herd’s own records.",
};

export default function ProjectArtwork({
  project,
  compact = false,
}: {
  project: Project;
  compact?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const id = `va-${useId().replace(/:/g, "")}`;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!("IntersectionObserver" in window)) {
      setPlaying(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { rootMargin: "80px", threshold: 0.05 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`venture-art venture-art-${project.id}${compact ? " venture-art-compact" : ""}`}
      data-playing={playing}
      role="img"
      aria-label={descriptions[project.id]}
    >
      <svg
        className="venture-art-scene"
        viewBox="-10 -8 520 535"
        aria-hidden="true"
        focusable="false"
      >
        {project.id === "gruvi" ? (
          <GruviScene id={id} />
        ) : project.id === "fable" ? (
          <FableScene id={id} />
        ) : (
          <VeldScene id={id} />
        )}
      </svg>
    </div>
  );
}
